// Sequential desktop setup block (roadmap #100, second and third follow-ups).
// Staff-only: rendered exclusively from StaffDashboard.tsx, never from the public
// guest call flow.
//
// SEQUENTIAL disclosure (second operator feedback round): before the app is
// installed, only step 1 renders - unnumbered, since there is no visible second
// step yet. Once useInstallPrompt() reports installed (also true when already
// running in standalone display-mode), step 1 collapses to a numbered confirmation
// and step 2 (auto-start) appears.
//
// STATEFUL step 2 (third operator feedback round): downloading the setup file used
// to change nothing, so it could be re-downloaded forever with no sense of
// progress. A web page cannot read the registry or the Startup folder, so the step
// tracks its lifecycle with the persistent flags in staff/setupState.ts:
//
//   pending    → download button + explanation (previous behaviour).
//   downloaded → "double-click it and accept the system prompt" + an explicit
//                "I have run it" confirmation + a secondary re-download.
//   confirmed  → enabled; will be checked automatically on the next sign-in.
//   verified   → a window launch carried the ?autostart=1 marker the setup file
//                plants in the Startup shortcut - real proof it runs (highest
//                precedence, set in the bootstrap via captureAutostartMarker).
//
// Re-downloading from confirmed/verified never downgrades the state (getSetupState
// applies precedence). Step 1 reuses the existing install affordance
// (InstallAppButton / useInstallPrompt) as-is, including its browser-support
// fallback screen.
import { useState } from 'react'
import { t } from '../i18n'
import InstallAppButton from './InstallAppButton'
import { useInstallPrompt } from './installPrompt'
import { downloadSetupScript } from './setupScript'
import { getSetupState, markSetupConfirmed, markSetupDownloaded } from './setupState'

const DesktopSetup = () => {
  const { installed } = useInstallPrompt()
  const [setupState, setSetupState] = useState(getSetupState)

  const onDownload = () => {
    downloadSetupScript()
    markSetupDownloaded()
    setSetupState(getSetupState())
  }

  const onConfirmRun = () => {
    markSetupConfirmed()
    setSetupState(getSetupState())
  }

  if (!installed) {
    return (
      <div className="cf-desktop-setup">
        <p className="cf-desktop-setup-heading">{t.desktopSetupHeading}</p>
        <div className="cf-desktop-setup-step">
          <span className="cf-desktop-setup-step-label">{t.desktopSetupInstallLabel}</span>
          <p className="cf-desktop-setup-hint">{t.desktopSetupInstallHint}</p>
          <InstallAppButton />
        </div>
      </div>
    )
  }

  return (
    <div className="cf-desktop-setup">
      <p className="cf-desktop-setup-heading">{t.desktopSetupHeading}</p>

      <div className="cf-desktop-setup-step">
        <p className="cf-desktop-setup-installed">
          <span className="cf-desktop-setup-step-number">1</span>
          {t.desktopSetupInstalled}
        </p>
      </div>

      <div className="cf-desktop-setup-step">
        <span className="cf-desktop-setup-step-label">
          <span className="cf-desktop-setup-step-number">2</span>
          {t.desktopSetupStep2Label}
        </span>

        {setupState === 'pending' && (
          <>
            <p className="cf-desktop-setup-hint">{t.desktopSetupStep2Hint}</p>
            <div className="cf-help">
              <button type="button" className="cf-btn cf-btn-secondary" onClick={onDownload}>
                {t.desktopSetupDownload}
              </button>
            </div>
          </>
        )}

        {setupState === 'downloaded' && (
          <>
            <p className="cf-desktop-setup-hint">{t.desktopSetupDownloaded}</p>
            <div className="cf-help">
              <button type="button" className="cf-btn cf-btn-primary" onClick={onConfirmRun}>
                {t.desktopSetupConfirmRun}
              </button>
              <button type="button" className="cf-btn cf-btn-secondary" onClick={onDownload}>
                {t.desktopSetupDownloadAgain}
              </button>
            </div>
          </>
        )}

        {(setupState === 'confirmed' || setupState === 'verified') && (
          <>
            <p className="cf-desktop-setup-confirmed">
              {setupState === 'verified' ? t.desktopSetupVerified : t.desktopSetupConfirmed}
            </p>
            <button type="button" className="cf-desktop-setup-redownload" onClick={onDownload}>
              {t.desktopSetupDownloadAgain}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default DesktopSetup
